import { useState, useEffect } from 'react'
import { X, DollarSign, Calendar, CreditCard, Banknote, Building2, FileText } from 'lucide-react'
import { formatCurrency } from '../../utils/formatters'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import useInvoiceStore from '../../stores/useInvoiceStore'

/**
 * Add Payment Modal Component
 * نافذة إضافة دفعة جديدة للفاتورة
 *
 * Features:
 * - Amount input with validation
 * - Payment method selection (4 options)
 * - Payment date picker
 * - Optional notes
 * - Max amount validation (remaining balance)
 * - Real-time validation feedback
 *
 * Protocols Applied:
 * - P-YNEX-FORM-001: Form structure
 * - P-YNEX-CSS-001: Tailwind CSS usage
 * - P-VALIDATION-001: Validate all inputs
 * - P-UX-001: Clear user feedback
 * - P-RTL-001: RTL support
 * - P-DARK-MODE-001: Dark mode support
 */
const AddPaymentModal = ({ invoiceTotal, remainingBalance, onConfirm, onCancel }) => {
  // Get payment methods from store
  const { paymentMethods: apiPaymentMethods, fetchPaymentMethods, isLoadingPaymentMethods } = useInvoiceStore()

  // Helper function to format date as DD-MM-YY
  const formatDateToDDMMYY = (date) => {
    const d = new Date(date)
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = String(d.getFullYear()).slice(-2)
    return `${day}-${month}-${year}`
  }

  const [formData, setFormData] = useState({
    amount: remainingBalance ? parseFloat(remainingBalance.toFixed(3)) : '', // Round to 3 decimals for KWD
    paymentMethod: 'cash',
    paymentDate: formatDateToDDMMYY(new Date()), // Today's date in DD-MM-YY
    notes: ''
  })

  const [errors, setErrors] = useState({})

  // Fetch payment methods on mount
  useEffect(() => {
    if (apiPaymentMethods.length === 0) {
      fetchPaymentMethods()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Icon mapping for payment methods
  const iconMapping = {
    'cash': Banknote,
    'credit_card': CreditCard,
    'bank_transfer': Building2,
    'cheque': FileText,
    'e_wallet': CreditCard
  }

  // Color mapping for payment methods
  const colorMapping = {
    'cash': 'text-green-600 dark:text-green-400',
    'credit_card': 'text-blue-600 dark:text-blue-400',
    'bank_transfer': 'text-purple-600 dark:text-purple-400',
    'cheque': 'text-orange-600 dark:text-orange-400',
    'e_wallet': 'text-indigo-600 dark:text-indigo-400'
  }

  // Build payment methods list from API or fallback to hardcoded
  const paymentMethods = apiPaymentMethods.length > 0
    ? apiPaymentMethods.map(method => ({
        value: method.value,
        label: method.name, // Use Arabic name from database
        description: method.name_e || method.name, // Use English name or fallback
        icon: iconMapping[method.value] || CreditCard,
        color: colorMapping[method.value] || 'text-gray-600 dark:text-gray-400',
        requires_reference: method.requires_reference
      }))
    : [
        {
          value: 'cash',
          label: 'نقدي',
          description: 'الدفع النقدي',
          icon: Banknote,
          color: 'text-green-600 dark:text-green-400'
        },
        {
          value: 'credit_card',
          label: 'بطاقة ائتمان',
          description: 'الدفع بالبطاقة',
          icon: CreditCard,
          color: 'text-blue-600 dark:text-blue-400'
        },
        {
          value: 'bank_transfer',
          label: 'تحويل بنكي',
          description: 'تحويل من البنك',
          icon: Building2,
          color: 'text-purple-600 dark:text-purple-400'
        },
        {
          value: 'cheque',
          label: 'شيك',
          description: 'الدفع بشيك',
          icon: FileText,
          color: 'text-orange-600 dark:text-orange-400'
        },
        {
          value: 'e_wallet',
          label: 'محفظة إلكترونية',
          description: 'الدفع الإلكتروني',
          icon: CreditCard,
          color: 'text-indigo-600 dark:text-indigo-400'
        }
      ]

  // Validate form
  const validate = () => {
    const newErrors = {}

    // Amount validation
    const amount = parseFloat(formData.amount)
    if (!formData.amount) {
      newErrors.amount = '⚠️ المبلغ مطلوب - الرجاء إدخال قيمة'
    } else if (isNaN(amount)) {
      newErrors.amount = '⚠️ يجب إدخال رقم صحيح فقط'
    } else if (amount <= 0) {
      newErrors.amount = '⚠️ المبلغ يجب أن يكون أكبر من صفر'
    } else if (amount > remainingBalance) {
      newErrors.amount = `⚠️ المبلغ يتجاوز المتبقي! الحد الأقصى: ${formatCurrency(remainingBalance)}`
    } else if (amount < 0.001) {
      newErrors.amount = '⚠️ المبلغ صغير جداً - أدنى قيمة: 0.001 د.ك'
    }

    // Payment date validation
    if (!formData.paymentDate) {
      newErrors.paymentDate = '⚠️ تاريخ الدفع مطلوب - الرجاء اختيار تاريخ'
    } else {
      // Validate format DD-MM-YY
      const datePattern = /^(\d{2})-(\d{2})-(\d{2})$/
      const match = formData.paymentDate.match(datePattern)

      if (!match) {
        newErrors.paymentDate = '⚠️ تنسيق التاريخ خاطئ! استخدم: DD-MM-YY (مثال: 11-12-25)'
      } else {
        const [, day, month, year] = match.map(Number)
        const fullYear = year < 100 ? 2000 + year : year

        // Validate date values
        if (month < 1 || month > 12) {
          newErrors.paymentDate = '⚠️ الشهر غير صحيح! يجب أن يكون بين 01 و 12'
        } else if (day < 1 || day > 31) {
          newErrors.paymentDate = '⚠️ اليوم غير صحيح! يجب أن يكون بين 01 و 31'
        } else {
          const selectedDate = new Date(fullYear, month - 1, day)

          // Check if date is valid (handles Feb 30, etc.)
          if (selectedDate.getMonth() !== month - 1) {
            newErrors.paymentDate = '⚠️ هذا التاريخ غير موجود في التقويم!'
          } else {
            const today = new Date()
            today.setHours(23, 59, 59, 999) // End of today

            if (selectedDate > today) {
              newErrors.paymentDate = '⚠️ لا يمكن اختيار تاريخ مستقبلي! اختر تاريخ اليوم أو قبله'
            }
          }
        }
      }
    }

    // Payment Method validation
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = '⚠️ طريقة الدفع مطلوبة - الرجاء الاختيار'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault()

    if (validate()) {
      // Parse date in DD-MM-YY format to ISO string
      const [day, month, year] = formData.paymentDate.split('-').map(Number)
      const fullYear = year < 100 ? 2000 + year : year
      const dateObj = new Date(fullYear, month - 1, day)

      // Find payment method ID from the selected value
      const selectedMethod = apiPaymentMethods.find(m => m.value === formData.paymentMethod)

      onConfirm({
        amount: parseFloat(formData.amount),
        payment_method_id: selectedMethod ? selectedMethod.id : null,
        paymentDate: dateObj.toISOString(),
        notes: formData.notes.trim()
      })
    }
  }

  // Handle input change
  const handleChange = (field, value) => {
    // Auto-format date input as DD-MM-YY
    if (field === 'paymentDate') {
      // Remove non-digits
      let cleaned = value.replace(/\D/g, '')

      // Auto-add dashes
      if (cleaned.length >= 2) {
        cleaned = cleaned.slice(0, 2) + '-' + cleaned.slice(2)
      }
      if (cleaned.length >= 5) {
        cleaned = cleaned.slice(0, 5) + '-' + cleaned.slice(5, 7)
      }

      value = cleaned.slice(0, 8) // DD-MM-YY max 8 chars
    }

    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6 shadow-xl animate-scale-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              إضافة دفعة جديدة
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              المبلغ المتبقي: <span className="font-semibold text-primary-600 dark:text-primary-400">
                {formatCurrency(remainingBalance)}
              </span>
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            aria-label="إغلاق"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              المبلغ <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <DollarSign className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                step="0.01"
                min="0"
                max={remainingBalance}
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                placeholder="0.00"
                className={`
                  w-full pr-10 pl-4 py-2 border rounded-lg
                  bg-white dark:bg-gray-700
                  text-gray-900 dark:text-white
                  placeholder-gray-400 dark:placeholder-gray-500
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.amount
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                  }
                `}
              />
            </div>
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
            )}
          </div>

          {/* Payment Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              طريقة الدفع <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {paymentMethods.map((method) => {
                const Icon = method.icon
                return (
                  <label
                    key={method.value}
                    className={`
                      flex items-center gap-2 p-3 border rounded-lg cursor-pointer
                      transition-all
                      ${formData.paymentMethod === method.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.value}
                      checked={formData.paymentMethod === method.value}
                      onChange={(e) => handleChange('paymentMethod', e.target.value)}
                      className="sr-only"
                    />
                    <Icon className={`w-5 h-5 ${method.color}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {method.label}
                      </p>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Payment Date - React DatePicker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              تاريخ الدفع <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
              <DatePicker
                selected={formData.paymentDate ? (() => {
                  // Convert DD-MM-YY to Date object
                  const match = formData.paymentDate.match(/^(\d{2})-(\d{2})-(\d{2})$/)
                  if (match) {
                    const [, day, month, year] = match
                    const fullYear = parseInt(year) < 100 ? 2000 + parseInt(year) : parseInt(year)
                    return new Date(fullYear, parseInt(month) - 1, parseInt(day))
                  }
                  return new Date()
                })() : new Date()}
                onChange={(date) => {
                  if (date) {
                    // Convert Date to DD-MM-YY
                    const day = String(date.getDate()).padStart(2, '0')
                    const month = String(date.getMonth() + 1).padStart(2, '0')
                    const year = String(date.getFullYear()).slice(-2)
                    handleChange('paymentDate', `${day}-${month}-${year}`)
                  }
                }}
                dateFormat="dd-MM-yy"
                maxDate={new Date()} // لا يمكن اختيار تاريخ مستقبلي
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={10}
                placeholderText="اختر التاريخ"
                className={`
                  w-full pr-10 pl-4 py-2 border rounded-lg
                  bg-white dark:bg-gray-700
                  text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  cursor-pointer
                  ${errors.paymentDate
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                  }
                `}
                wrapperClassName="w-full"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              التنسيق: DD-MM-YY (يوم-شهر-سنة)
            </p>
            {errors.paymentDate && (
              <p className="text-red-500 text-sm mt-1 font-medium">{errors.paymentDate}</p>
            )}
          </div>

          {/* Notes (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              ملاحظات (اختياري)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="أي ملاحظات إضافية..."
              rows="3"
              className="
                w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                bg-white dark:bg-gray-700
                text-gray-900 dark:text-white
                placeholder-gray-400 dark:placeholder-gray-500
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                resize-none
              "
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary flex-1"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1"
            >
              إضافة الدفعة
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddPaymentModal
